import { Box, Chip, Typography } from '@mui/material';
import { configService } from '@/services/configService';

interface CharacterCounterProps {
  /** 현재 문자 수 / Current character count */
  currentCount: number;
  /** 최대 문자 수 (선택사항) / Maximum character count (optional) */
  maxCount?: number;
  /** 에러 상태 (3000자 초과 등) / Error state (exceeds 3000 characters etc.) */
  isError?: boolean;
  /** 표시할 라벨 (예: "자", "characters") / Label to display (e.g., "자", "characters") */
  label?: string;
  /** 한글 자수 표시 여부 (현재 자수만 표시) / Show Korean character count only (current count only) */
  isKoreanOnly?: boolean;
  /** 설정 타입 (text, image, translation) / Config type for dynamic limits */
  configType?: 'text' | 'image' | 'translation';
}

const CharacterCounter = ({
  currentCount,
  maxCount,
  isError = false,
  label = '자',
  isKoreanOnly = false,
  configType,
}: CharacterCounterProps) => {
  // 동적 설정값 가져오기 / Get dynamic config values
  const getDynamicMaxCount = (): number => {
    if (maxCount !== undefined) {
      return maxCount;
    }
    
    if (configType) {
      const config = configService.getConfigLimits();
      switch (configType) {
        case 'text':
          return config.textMaxPromptLength;
        case 'image':
          return config.imageMaxPromptLength;
        case 'translation':
          return config.translationMaxOutputLength;
        default:
          return 3000;
      }
    }
    
    return 3000; // 기본값 / Default value
  };

  const dynamicMaxCount = getDynamicMaxCount();
  const isOverLimit = currentCount > dynamicMaxCount;
  // 한글 전용 모드인 경우 현재 자수만 표시 / Korean only mode - show current count only
  if (isKoreanOnly) {
    return (
      <Chip
        label={
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontSize: '10px',
              fontWeight: 'bold',
            }}
          >
            {currentCount} {label}
          </Typography>
        }
        sx={{
          bgcolor: 'grey.200',
          height: 'auto',
          py: 0,
          px: 1,
          borderRadius: 5,
          '.MuiChip-label': {
            p: 0,
            lineHeight: 1,
          },
        }}
      />
    );
  }

  // 일반 모드 (최대 자수 표시) / Normal mode (show max count)
  return (
    <Chip
      label={
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: isOverLimit ? 'white' : 'primary.main',
              fontSize: '10px',
              fontWeight: 'bold',
            }}
          >
            {currentCount}
          </Typography>
          <Typography
            variant="caption"
            sx={{ 
              color: isOverLimit ? 'white' : 'text.secondary', 
              fontSize: '10px' 
            }}
          >
            / {dynamicMaxCount.toLocaleString()}{label}
          </Typography>
        </Box>
      }
      sx={{
        bgcolor: isOverLimit 
          ? theme => theme.palette.error.light 
          : 'grey.200',
        height: 'auto',
        py: 0,
        px: 1,
        borderRadius: 5,
        '.MuiChip-label': {
          p: 0,
          lineHeight: 1,
        },
      }}
    />
  );
};

export default CharacterCounter;
